import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { RETRACTABLE_COMPARE } from "../lib/subcategoryGuides.mjs";
import { productImage } from "../lib/api";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);

/* Side-by-side table of the 6 retractable stands. `products` = live list on the page (for prices/images). */
export function RetractableCompare({ products = [] }) {
  const bySlug = Object.fromEntries(products.map((p) => [p.slug, p]));
  const rows = RETRACTABLE_COMPARE.map((r) => {
    const p = bySlug[r.slug];
    const from = p ? Math.min(...[(p.tier_pricing || []).map((t) => t.unit_price ?? t.price), p.base_price].flat().filter((x) => x != null)) : null;
    return { ...r, from, img: r.image || (p?.image_url ? productImage(p.image_url) : ""), name: (p?.name || r.short).trim() };
  });
  return (
    <section className="mb-10" data-testid="retractable-compare">
      <div className="overline mb-1">Compare the six stands</div>
      <h2 className="font-display font-black text-2xl text-kolt-slate-dark tracking-tight mb-4">Which retractable banner stand do I need?</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        {rows.map((r) => (
          <Link key={r.slug} to={`/shop/product/${r.slug}`} className="group bg-white border border-slate-200 hover:border-kolt-orange transition-colors" data-testid={`compare-card-${r.slug}`}>
            <div className="aspect-[2/3] bg-slate-50 overflow-hidden">
              {r.img && <img src={r.img} alt={`${r.name} retractable banner stand`} width="400" height="600" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />}
            </div>
            <div className="p-3">
              <div className="font-mono text-[9px] tracking-widest uppercase text-kolt-orange">{r.tag}</div>
              <div className="font-bold text-sm text-kolt-slate-dark leading-tight mt-0.5">{r.short}</div>
              <div className="text-xs text-slate-500 mt-1">from <span className="font-bold text-kolt-slate-dark" data-testid={`compare-price-${r.slug}`}>{money(r.from)}</span></div>
            </div>
          </Link>
        ))}
      </div>
      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full text-sm text-left" data-testid="retractable-compare-table">
          <thead className="bg-slate-50 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
            <tr><th className="px-3 py-2">Stand</th><th className="px-3 py-2">Graphic size</th><th className="px-3 py-2">Material</th><th className="px-3 py-2">Included hardware</th><th className="px-3 py-2">Sides</th><th className="px-3 py-2">Weight</th><th className="px-3 py-2">Best for</th><th className="px-3 py-2">From</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.slug} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2"><Link to={`/shop/product/${r.slug}`} className="font-bold text-kolt-slate-dark hover:text-kolt-orange inline-flex items-center gap-1">{r.short} <ArrowRight className="w-3 h-3" /></Link></td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.size}</td>
                <td className="px-3 py-2 text-slate-600">{r.material}</td>
                <td className="px-3 py-2 text-slate-600">{r.hardware}</td>
                <td className="px-3 py-2 text-slate-600">{r.sides}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.weight}</td>
                <td className="px-3 py-2 text-slate-600">{r.best}</td>
                <td className="px-3 py-2 font-bold text-kolt-slate-dark whitespace-nowrap">{money(r.from)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-2">Prices are the lowest per-piece tier; live quantity pricing on each product page. Replacement graphics for stands you already own: request a quote.</p>
    </section>
  );
}
